import React from "react";
import styled, { css } from "styled-components";
import { getIconName, iconName } from "../icons/Icons";
import { ButtonVariant } from "./Shared";
import { TooltipTheOneWrapper } from "../TooltipTheOne";
import { button2Styles, ButtonVariantStyles } from "../styles/button2Styles";
import { ProtectedPageContext } from "../../pages/_contexts/ProtectedPageContext";

type ButtonType = 'button' | 'submit' | 'reset';

export type Button2Props = {
    disabled?: boolean;
    icon?: iconName;
    iconPosition?: 'left' | 'right';
    innerRef?:React.Ref<HTMLButtonElement>;
    text?: string;
    toolTipText?:string;
    tooltipHorizontalAlignment?: 'left' | 'right';
    type?:ButtonType;
    variant:ButtonVariant;
    onClick?:React.MouseEventHandler<HTMLButtonElement>
}
export function Button2(props: Button2Props){

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            props.onClick(event);  // Trigger the onClick function when Enter is pressed and pass the event object as an argument
        }
      };

    const colors = React.useContext(ProtectedPageContext).colors;

    const buttonStyles = button2Styles(colors);
    const variantStyles = buttonStyles.variants[props.variant];

    const btn =
        <StyledButton
            height={"32px"}
            variant={props.variant}
            borderRadius={buttonStyles.borderRadius}
            onKeyDown={handleKeyDown}
            ref={props.innerRef}
            styles={variantStyles}
            icon={props.icon}
            iconPosition={props.iconPosition}
            disabled={props.disabled}
            onClick={ (e) => props.onClick!=undefined && props.onClick(e)}>
            {props.icon && (
                <IconWrapper variant={props.variant} styles={variantStyles}>{getIconName(props.icon)}</IconWrapper>
            )}
            {props.text && (
                <span>{props.text}</span>
            )}
        </StyledButton>;

    return (
        <>
            {props.toolTipText 
                ?   <TooltipTheOneWrapper tooltipContent={props.toolTipText} horizontalAlignment={props.tooltipHorizontalAlignment}>
                        {btn}
                    </TooltipTheOneWrapper>
                : btn
            }
        </>
    );
}


const IconWrapper = styled.div<{ variant: ButtonVariant, styles: ButtonVariantStyles, disabled?:boolean }>`
    position: relative; 
    height: 20px;
    z-index: 101;

    & svg {
        height: 20px;
        width: 20px;
        fill: ${props => props.styles.default.color};
    }
`;

const StyledButton = styled.button<{ 
    height:string, 
    variant: ButtonVariant,
    borderRadius:string,
    styles: ButtonVariantStyles,
    icon?:iconName,
    iconPosition?:string,
    disabled?:boolean
}>`
    display:inline-flex;
    flex-direction: ${props => props.iconPosition === "right" ? "row-reverse" : "row"};
    position: relative;
    align-items: center;
    justify-content: center;
    white-space: nowrap;

    height: ${props => props.height };
    padding: 1px 8px;

    font-size: 16px;
    font-family: "Noto Sans", sans-serif;
    font-weight: 400;
    line-height: 1.2;
    letter-spacing: 0.25px;
    text-decoration: none;

    background: ${props => props.styles.default.background };
    color: ${props => props.styles.default.color};
    
    border: ${props => props.styles.default.border };
    border-radius: ${props => props.borderRadius };

    transition: background-color 0.15s ease-out, 
        color 0.15s ease-out, 
        border-color 0.15s ease-out;

    & svg {
        transition: fill 0.15s ease-out;
    }

    &:hover, &:focus-visible {
        background: ${props => props.styles.hover.background };
        color: ${props =>  props.styles.hover.color };
        border: ${props => props.styles.hover.border };
        cursor: pointer;
        & svg{
            fill: ${props => props.styles.hover.color};
        }
    }

    &:active{
        background: ${props => props.styles.active.background };
        color: ${props =>  props.styles.active.color };
        border: ${props => props.styles.active.border };
    }


    ${props => {
        
        if(props.disabled){
            return css`
                background: ${props.styles.disabled.background };
                color: ${props.styles.disabled.color };
                border: ${props.styles.disabled.border };

                 & svg {
                    fill: ${props.styles.disabled.color};
                }

                &:hover, &:focus-visible {
                    background: ${props.styles.disabled.background };
                    color: ${ props.styles.disabled.color };
                    border: ${props.styles.disabled.border };
                    cursor: not-allowed;
                    & svg{
                        fill: ${props.styles.disabled.color};
                    }
                }
            `;
        }
    }}
`;